import React from 'react';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

export default class EhcParticipantsImport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      loading: false,
      polling: false,
      cancelling: false,
      dataImportId: null,
      errors: [],
      skipped: [],
      counts: null,
      successMessage: null,
      processingNotice: null,
      importComplete: false,
    };
    this.fileInputRef = React.createRef();
    this.pollIntervalId = null;
    this.consecutivePollFailures = 0;
  }

  componentDidMount() {
    this.resumeActiveImport();
  }

  componentWillUnmount() {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  // After a page refresh the in-flight poll state is lost, so re-discover any import still
  // pending/processing for this service order and resume polling (which re-shows the cancel
  // control), instead of leaving the user with a stuck import they cannot see or abort.
  resumeActiveImport = () => {
    const { importActiveUrl, serviceOrderId } = this.props;
    if (!importActiveUrl) return;

    fetch(`${importActiveUrl}?service_order_id=${serviceOrderId}`)
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        if (data.data_import_id && (data.status === 'pending' || data.status === 'processing')) {
          this.startPolling(data.data_import_id);
        }
      })
      .catch(() => {});
  };

  label = (key, fallback) => (this.props.labels && this.props.labels[key]) || fallback;

  handleFileChange = (e) => {
    this.setState({
      file: e.target.files[0],
      errors: [],
      skipped: [],
      successMessage: null,
      processingNotice: null,
      importComplete: false,
    });
  };

  formatSuccessMessage = (addedCount, updatedCount) => {
    const labels = this.props.labels || {};
    if (addedCount > 0 && updatedCount > 0) {
      const template = labels.import_success_mixed || '__ADDED__ peserta ditambahkan, __UPDATED__ diperbarui.';
      return template
        .replace('__ADDED__', addedCount)
        .replace('__UPDATED__', updatedCount)
        .replace('%{added}', addedCount)
        .replace('%{updated}', updatedCount);
    }
    if (addedCount > 0) {
      const template = labels.import_success_added || '__COUNT__ peserta ditambahkan.';
      return template.replace('__COUNT__', addedCount).replace('%{count}', addedCount);
    }
    const template = labels.import_success_updated || '__COUNT__ peserta diperbarui.';
    return template.replace('__COUNT__', updatedCount).replace('%{count}', updatedCount);
  };

  formatPartialMessage = (importedCount, addedCount, updatedCount, failedCount) => {
    const template = this.props.labels?.import_partial_success || '__IMPORTED__ berhasil (__ADDED__ ditambahkan, __UPDATED__ diperbarui), __FAILED__ gagal';
    return template
      .replace('__IMPORTED__', importedCount)
      .replace('__ADDED__', addedCount)
      .replace('__UPDATED__', updatedCount)
      .replace('__FAILED__', failedCount)
      .replace('%{imported}', importedCount)
      .replace('%{added}', addedCount)
      .replace('%{updated}', updatedCount)
      .replace('%{failed}', failedCount);
  };

  finishImport = (data) => {
    const addedCount = data.added_count || 0;
    const updatedCount = data.updated_count || 0;
    const importedCount = data.imported_count || (addedCount + updatedCount);
    const errors = data.errors || [];
    const skipped = data.skipped || [];
    const hasFailures = errors.length > 0;
    const importComplete = importedCount > 0 || (!hasFailures && skipped.length > 0);

    let successMessage = null;
    if (importedCount > 0 && hasFailures) {
      successMessage = this.formatPartialMessage(importedCount, addedCount, updatedCount, errors.length);
    } else if (importedCount > 0) {
      successMessage = this.formatSuccessMessage(addedCount, updatedCount);
    }

    this.setState({
      file: null,
      loading: false,
      successMessage,
      counts: { added: addedCount, updated: updatedCount, skipped: skipped.length },
      errors: hasFailures ? errors : [],
      skipped,
      importComplete,
    });

    if (this.fileInputRef.current) this.fileInputRef.current.value = '';

    if (importedCount > 0 && this.props.onSuccess) {
      this.props.onSuccess({ importedCount });
    }
  };

  handleSubmit = () => {
    const { file } = this.state;
    const { importUrl } = this.props;

    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('service_order_id', this.props.serviceOrderId);

    this.setState({
      loading: true,
      polling: false,
      counts: null,
      errors: [],
      skipped: [],
      successMessage: null,
      processingNotice: null,
      importComplete: false,
    });

    fetch(importUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      body: formData,
    })
      .then((res) => {
        if (!res.ok && res.status !== 422) {
          return res.text().then((text) => {
            throw new Error(`Server error ${res.status}: ${text.substring(0, 200)}`);
          });
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          this.startPolling(data.data_import_id);
        } else {
          this.setState({ errors: data.errors || [data.message || this.label('import_failed_generic', 'Import gagal')], file: null, loading: false });
          if (this.fileInputRef.current) this.fileInputRef.current.value = '';
          // Submit rejected because an import is already running: surface it as a resumable,
          // cancelable job instead of a dead-end error.
          this.resumeActiveImport();
        }
      })
      .catch((err) => {
        this.setState({ errors: [err.message || this.label('import_error_generic', 'Terjadi kesalahan. Coba lagi.')], file: null, loading: false });
        if (this.fileInputRef.current) this.fileInputRef.current.value = '';
      });
  };

  startPolling = (dataImportId) => {
    const { importStatusUrl } = this.props;
    const pollStartedAt = Date.now();

    this.consecutivePollFailures = 0;
    this.setState({ polling: true, loading: true, processingNotice: null, dataImportId });

    this.pollIntervalId = setInterval(() => {
      if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
        this.stopPolling();
        // Still running server-side: this is not a participant failure. The completion
        // notification links to the import detail page with the full validation errors.
        this.setState({ processingNotice: this.label('import_timeout_notice', 'Masih diproses, cek notifikasi'), file: null, loading: false });
        if (this.fileInputRef.current) this.fileInputRef.current.value = '';
        return;
      }

      fetch(`${importStatusUrl}?service_order_id=${this.props.serviceOrderId}&data_import_id=${dataImportId}`)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Server error ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          this.consecutivePollFailures = 0;

          if (data.status === 'success' || data.status === 'failed') {
            this.stopPolling();
            this.finishImport(data);
          }
        })
        .catch((err) => {
          this.consecutivePollFailures += 1;
          if (this.consecutivePollFailures < MAX_CONSECUTIVE_POLL_FAILURES) return;

          this.stopPolling();
          this.setState({ errors: [err.message || this.label('import_error_generic', 'Terjadi kesalahan. Coba lagi.')], file: null, loading: false });
          if (this.fileInputRef.current) this.fileInputRef.current.value = '';
        });
    }, POLL_INTERVAL_MS);
  };

  stopPolling = () => {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
    this.setState({ polling: false });
  };

  handleCancel = () => {
    const { importCancelUrl, serviceOrderId } = this.props;
    const { dataImportId } = this.state;
    if (!importCancelUrl || !dataImportId) return;

    this.setState({ cancelling: true });

    fetch(importCancelUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body: JSON.stringify({ service_order_id: serviceOrderId, data_import_id: dataImportId }),
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        this.stopPolling();
        const cancelledLabel = this.label('import_cancelled', 'Import dibatalkan.');
        const failed = !data.success;
        this.setState({
          cancelling: false,
          loading: false,
          file: null,
          dataImportId: null,
          successMessage: failed ? null : cancelledLabel,
          errors: failed ? [data.message || cancelledLabel] : [],
        });
        if (this.fileInputRef.current) this.fileInputRef.current.value = '';
      })
      .catch(() => {
        this.stopPolling();
        this.setState({ cancelling: false, loading: false });
      });
  };

  render() {
    const { file, loading, polling, cancelling, errors, skipped, counts, successMessage, processingNotice, importComplete } = this.state;
    const showOverlay = loading || polling;
    const dismissLabel = this.label('import_dismiss', 'Tutup');
    const skippedTitle = this.label('import_skipped_title', 'Baris dilewati:');
    const processingLabel = polling ? this.label('import_processing', 'Memproses...') : this.label('import_uploading', 'Mengimport...');

    return (
      <div style={{ position: 'relative' }}>
        {showOverlay && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              background: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div className="loading-wheel style-2" />
            <div className="mt-2 font-weight-bold">
              {processingLabel}
            </div>
            {polling && this.props.importCancelUrl &&
              <button
                type="button"
                className="btn btn-outline-danger btn-sm mt-3"
                onClick={this.handleCancel}
                disabled={cancelling}
              >
                <i className="zmdi zmdi-close-circle mr-1" />
                {cancelling
                  ? this.label('import_cancelling', 'Membatalkan...')
                  : this.label('import_cancel_button', 'Batalkan Import')}
              </button>
            }
          </div>
        )}

        <div className="mb-3">
          <a
            href={this.props.templateUrl}
            className="btn btn-outline-secondary btn-sm"
            download
          >
            <i className="zmdi zmdi-download mr-1" />
            {this.label('import_download_template', 'Download Template Excel')}
          </a>
        </div>

        {!importComplete &&
          <div className="form-group">
            <label>{this.label('import_file_label', 'File Excel (.xlsx)')}</label>
            <input
              ref={this.fileInputRef}
              type="file"
              accept=".xlsx"
              className="form-control"
              onChange={this.handleFileChange}
              disabled={loading}
            />
            <i className="form-group__bar" />
            <small className="text-muted">
              {this.label('import_format_hint', 'Format kolom: full_name, nik, email, phone_number, date_of_birth, employment_id_number, hospital_name, service_date, is_covered, show_result, additional_tests_*')}
            </small>
          </div>
        }

        {(successMessage || processingNotice || counts || errors.length > 0 || skipped.length > 0) &&
          <div className="import-result-alerts">
            {/* The backend already returns added / updated / skipped separately, so they
                are shown as three figures rather than one interpolated sentence. */}
            {counts &&
              <div className="import-result-counts">
                <div className="import-result-counts__tile import-result-counts__tile--added">
                  <span className="import-result-counts__value">{counts.added}</span>
                  <span className="import-result-counts__label">{this.label('import_count_added', 'Ditambahkan')}</span>
                </div>
                <div className="import-result-counts__tile import-result-counts__tile--updated">
                  <span className="import-result-counts__value">{counts.updated}</span>
                  <span className="import-result-counts__label">{this.label('import_count_updated', 'Diperbarui')}</span>
                </div>
                <div className="import-result-counts__tile import-result-counts__tile--skipped">
                  <span className="import-result-counts__value">{counts.skipped}</span>
                  <span className="import-result-counts__label">{this.label('import_count_skipped', 'Dilewati')}</span>
                </div>
              </div>
            }

            {successMessage &&
              <div className="alert alert-success mb-0" role="alert">
                <i className="zmdi zmdi-check-circle mr-2" />
                <strong>{successMessage}</strong>
              </div>
            }

            {processingNotice &&
              <div className="alert alert-info mb-0" role="status">
                <i className="zmdi zmdi-time mr-2" />
                {processingNotice}
              </div>
            }

            {errors.length > 0 && (
              <div className="alert alert-warning mb-0">
                <strong>{this.props.labels?.import_failed_title || 'Beberapa peserta gagal diimport:'}</strong>
                <ul className="mb-0 mt-1">
                  {errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {skipped.length > 0 && (
              <div className="alert alert-info mb-0">
                <strong>{skippedTitle}</strong>
                <ul className="mb-0 mt-1">
                  {skipped.map((message, i) => (
                    <li key={i}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        }

        <div className="d-flex justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-secondary mr-2"
            onClick={this.props.onClose}
            disabled={loading && !importComplete}
          >
            {importComplete ? dismissLabel : this.label('import_close', 'Batal')}
          </button>
          {!importComplete &&
            <button
              type="button"
              className="btn btn-primary"
              onClick={this.handleSubmit}
              disabled={!file || loading}
            >
              {showOverlay ? processingLabel : this.label('import_submit', 'Import')}
            </button>
          }
        </div>
      </div>
    );
  }
}
